import { Link } from "react-router-dom";

interface OverlayCard {
  title: string;
  description?: string;
  cta?: { text: string; href: string };
}

interface MediaHeroProps {
  /** Video URL or image URL */
  media: string;
  /** Set true if media is a video */
  video?: boolean;
  headline: string;
  subtext?: string;
  cta?: { text: string; href: string };
  overlayCards?: OverlayCard[];
  /** "tall" = full viewport height, "standard" = ~70vh */
  height?: "tall" | "standard";
}

const MediaHero = ({
  media,
  video = false,
  headline,
  subtext,
  cta,
  overlayCards,
  height = "standard",
}: MediaHeroProps) => {
  const heightClass = height === "tall" ? "min-h-screen" : "min-h-[70vh]";

  return (
    <section className={`relative w-full ${heightClass} flex items-end overflow-hidden`} aria-label={headline}>
      {/* Background media */}
      {video ? (
        <video
          src={media}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        />
      ) : (
        <img
          src={media}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          {...({ fetchpriority: "high" } as any)}
        />
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-rich-black/80 via-rich-black/40 to-rich-black/20" aria-hidden="true" />

      {/* Content */}
      <div className="relative z-10 w-full pb-16 pt-40 md:pb-20 md:pt-48">
        <div className="mx-auto max-w-wide px-5 md:px-12">
          <div className="max-w-2xl">
            <h1 className="font-headline text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-cream leading-[1.1] mb-4">
              {headline}
            </h1>
            {subtext && (
              <p className="text-cream/70 text-sm md:text-base leading-relaxed max-w-lg mb-8">
                {subtext}
              </p>
            )}
            {cta && (
              <Link
                to={cta.href}
                className="inline-block bg-accent text-accent-foreground font-body text-sm uppercase tracking-widest px-8 py-3 hover:bg-accent/90 transition-colors"
              >
                {cta.text}
              </Link>
            )}
          </div>

          {/* Overlay cards */}
          {overlayCards && overlayCards.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mt-8 md:mt-12">
              {overlayCards.map((card, i) => (
                <div
                  key={i}
                  className="bg-rich-black/50 backdrop-blur-md border border-cream/10 p-5"
                >
                  <h3 className="font-headline text-lg text-cream mb-2">{card.title}</h3>
                  {card.description && (
                    <p className="text-cream/50 text-sm leading-relaxed mb-3">
                      {card.description}
                    </p>
                  )}
                  {card.cta && (
                    <Link
                      to={card.cta.href}
                      className="font-body text-xs uppercase tracking-widest text-accent hover:text-accent/80 transition-colors"
                    >
                      {card.cta.text} →
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MediaHero;
